import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useQuery } from 'urql';
import { timeSlotsQuery } from '../../graphql/services';
import { Service } from './useServiceSelection';


interface TimeGroups {
  morning: string[];
  afternoon: string[];
  evening: string[];
}

interface UseDateTimeSelectionProps {
  selectedServices: Service[];
  selectedTimeZone: string;
  selectedLocation: any;
}

export const useDateTimeSelection = ({
  selectedServices,
  selectedTimeZone,
  selectedLocation,
}: UseDateTimeSelectionProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [portalId] = useState(`lp-date-picker-portal-${Math.random().toString(36).substr(2, 9)}`);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Portal container setup
  useEffect(() => {
    let container = document.getElementById(portalId);
    if (!container) {
      container = document.createElement('div');
      container.id = portalId;
      document.body.appendChild(container);
      setContainer(container);
    }

    return () => {
      const containerToRemove = document.getElementById(portalId);
      if (containerToRemove && containerToRemove.parentNode) {
        containerToRemove.parentNode.removeChild(containerToRemove);
      }
    };
  }, [portalId]);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Query for available time slots
  const [result, reexecuteQuery] = useQuery({
    query: timeSlotsQuery,
    pause: true,
    requestPolicy: 'network-only',
    variables: {
      date: selectedDate ? formatDate(selectedDate) : '',
      serviceIds: selectedServices.map((service) => service.id),
      locationId: selectedLocation !== null ? selectedLocation.id : null
    },
  });

  const { data, fetching, error } = result;

  // Fetch time slots when date changes
  useEffect(() => {
    if (selectedDate) {
      reexecuteQuery();
    }
  }, [selectedDate, reexecuteQuery]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  // Group time slots into morning, afternoon, evening
  const groupTimeSlots = (utcSlots: string[]): TimeGroups => {
    const groups: TimeGroups = {
      morning: [],
      afternoon: [],
      evening: []
    };

    const now = DateTime.now().setZone(selectedTimeZone);

    utcSlots.forEach(utcSlot => {
      // Convert UTC slot to local time
      const slotInLocalTime = DateTime.fromISO(utcSlot).setZone(selectedTimeZone);

      // Check if slot is in the past
      if (slotInLocalTime < now) return;

      // Format time as HH:mm
      const timeStr = slotInLocalTime.toFormat('HH:mm');
      const hour = slotInLocalTime.hour;

      // Group by time of day
      if (hour < 12) {
        groups.morning.push(timeStr);
      } else if (hour < 17) {
        groups.afternoon.push(timeStr);
      } else {
        groups.evening.push(timeStr);
      }
    });

    // Sort each group
    groups.morning.sort();
    groups.afternoon.sort();
    groups.evening.sort();

    return groups;
  };

  // Process time slots if data is available
  const timeGroups = data?.getAvailableSlots ? groupTimeSlots(data.getAvailableSlots) : null;
  const hasAvailableSlots = timeGroups ? (
    (timeGroups.morning && timeGroups.morning.length > 0) ||
    (timeGroups.afternoon && timeGroups.afternoon.length > 0) ||
    (timeGroups.evening && timeGroups.evening.length > 0)
  ) : false;

  return {
    // State
    selectedDate,
    selectedTime,
    portalId,
    container,
    isMobile,

    // Query results
    data,
    fetching,
    error,
    timeGroups,
    hasAvailableSlots,

    // Actions
    setSelectedDate,
    setSelectedTime,
    handleDateChange,

    // Helpers
    formatDate
  };
};
